import React, { Fragment, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import { LearnMore, InterestPayMoreMonthly } from 'src/constants/trackingEvents'
import AdditionalInfo from 'src/Components/AdditionalInfo'
import { Button, If, Text, Format, StatusIcon } from 'src/Components'
import { IconDollar, IconPencil, IconCheckCircle } from 'src/Components/Icon'
import usePrev from 'src/hooks/usePrev'
import ShowPaidExtra from './ShowPaidExtra/ShowPaidExtra'
import ShowInfo from './ShowInfo/ShowInfo'
import { useToggle } from 'src/hooks/useToggle'
import { HeightFade } from 'src/Components/Transition'
import { selectIsDashFocused } from 'src/store/selectors/home/homeowner'
import { ConfettiTarget } from 'src/Components/Confetti'

import styles from './Info.module.scss'

const Info = ({
  intl,
  withCurrentHomeData,
  loanDetails,
  updateLoanBalances,
  redrawHeightFade,
  hasRefiUPBFeature,
  balanceDiff,
  hasSavedUPB,
  upbRequested
}) => {
  const {
    on: showInfo,
    toggle: toggleShowInfo,
    reset: resetShowInfo
  } = useToggle()

  const {
    on: showPaidExtra,
    toggle: toggleShowPaidExtra,
    reset: resetShowPaidExtra
  } = useToggle()

  const learnMoreRef = useRef(null)
  const paidExtraRef = useRef(null)

  const prevBalanceDiff = usePrev(balanceDiff)
  const [explode, setExplode] = useState(false)

  const dashFocus = useSelector(selectIsDashFocused)
  useEffect(() => {
    if (!dashFocus && showPaidExtra) {
      resetShowPaidExtra()
    }
  }, [dashFocus, resetShowPaidExtra, showPaidExtra])

  useEffect(() => {
    if (typeof prevBalanceDiff === 'number') {
      setExplode(balanceDiff > prevBalanceDiff)
    }
  }, [prevBalanceDiff, balanceDiff])

  const infoToggleId = 'AdditionalInfo'

  const [submitted, setSubmitted] = useState(hasSavedUPB)

  const onSubmit = e => {
    e.preventDefault()

    let changed = false
    const data = loanDetails.reduce((acc, { uuid }) => {
      if (acc[uuid] !== e.target.elements[uuid].value) {
        changed = true
      }
      acc[uuid] = e.target.elements[uuid].value
      return acc
    }, {})

    if (changed) {
      updateLoanBalances(data)
      resetShowPaidExtra()
      setSubmitted(true)
    } else {
      resetShowPaidExtra()
    }
  }

  const onCancel = () => {
    resetShowPaidExtra()
  }

  const editPaidAmount = () => {
    setSubmitted(hasSavedUPB)
    redrawHeightFade(infoToggleId)
    toggleShowPaidExtra()
  }

  const handleLearnMoreClick = () => {
    toggleShowInfo()
    redrawHeightFade(infoToggleId)
    resetShowPaidExtra()
  }

  const handleAlreadyPaidExtraClick = () => {
    toggleShowPaidExtra()
    redrawHeightFade(infoToggleId)
    resetShowInfo()
  }

  const showSuccessBox = submitted && (balanceDiff > 0 || upbRequested)

  return (
    <Fragment>
      <Text
        align='center'
        className={classnames(
          showSuccessBox && styles.hideButtons,
          styles.infoButtonWrapper
        )}
      >
        <Button
          data-qa='info.learnMoreButton'
          ref={learnMoreRef}
          track={LearnMore}
          trackEnabled={withCurrentHomeData}
          trackOptions={{ eventLabel: InterestPayMoreMonthly }}
          onClick={handleLearnMoreClick}
        >
          <FormattedMessage
            id='Homeowner.interest.info.toggle'
            defaultMessage='Learn more about this'
          />
        </Button>
        <If condition={hasRefiUPBFeature}>
          <Button ref={paidExtraRef} onClick={handleAlreadyPaidExtraClick}>
            <FormattedMessage
              id='Homeowner.interest.loanBalance.toggle'
              defaultMessage='Already paid extra?'
            />
          </Button>
        </If>
      </Text>
      <AdditionalInfo
        visible={showInfo || showPaidExtra}
        cardWidth
        cardBottom
        targetRef={showInfo ? learnMoreRef : paidExtraRef}
      >
        <HeightFade id={infoToggleId}>
          <If condition={showInfo}>
            <ShowInfo intl={intl} withCurrentHomeData={withCurrentHomeData} />
          </If>
          <If condition={showPaidExtra}>
            <ShowPaidExtra
              loanDetails={loanDetails}
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </If>
        </HeightFade>
      </AdditionalInfo>
      <If condition={!showPaidExtra && showSuccessBox}>
        <ConfettiTarget go={!upbRequested && explode}>
          <div
            className={classnames(
              styles.extraPaymentSuccessContainer,
              upbRequested && styles.paidSoFarSpinning
            )}
          >
            <div className={styles.paidSoFarDollarIconWrapper}>
              <StatusIcon
                className={styles.paidSoFarDollarIcon}
                successIcon={IconDollar}
                success={!upbRequested}
                spin={upbRequested}
              />
            </div>
            <div className={styles.paidSuccessMessage}>
              <FormattedMessage
                id='Homeowner.interest.paidExtra.submitExtraSuccess'
                defaultMessage="You're paying down principal ahead of schedule - nice!"
              />
            </div>
            <div className={styles.amountPaidExtraSection}>
              <Text className={styles.amountPaidExtra}>
                <FormattedMessage
                  id='Homeowner.interest.paidExtra.amountPaidSoFar'
                  defaultMessage='{circleIcon} {amountPaidSoFar} extra paid so far'
                  values={{
                    amountPaidSoFar: (
                      <Text color='green' element='span'>
                        {<Format.Currency value={balanceDiff} format='0,0' />}
                      </Text>
                    ),
                    circleIcon: <IconCheckCircle color='green' />
                  }}
                />
              </Text>
              <Button minimal iconStart={IconPencil} onClick={editPaidAmount}>
                <FormattedMessage
                  id='Homeowner.interest.paidExtra.editButton'
                  defaultMessage='Update your balance'
                />
              </Button>
            </div>
          </div>
        </ConfettiTarget>
      </If>
    </Fragment>
  )
}

export default Info
